import React, { useState } from 'react';
import styled from 'styled-components';
import {
  colors,
  fonts,
  breakpoints,
  headingStyle,
  sectionPaddingStyle,
  containerStyle,
} from '../annuaire/designSystem';
import { fixerLiteServices } from '../../annuaire/bookingState';
import { icone } from '../../annuaire/tunnel/ui';
import {
  paliersDeLaFiche,
  euros,
  remiseMaxBonusRepar,
} from '../../annuaire/bonusRepar';

/**
 * Les prestations réservables du réparateur, avec leur durée et leur prix
 * (FIXERLIGHT-050, décision de Clément du 21/09).
 *
 * À ne pas confondre avec `ShopServices`, plus bas : celui-là affiche
 * `details.services`, les libellés Airtable de ce que fait l'atelier
 * (« Vente vélos neufs »), sans durée ni prix. Ici c'est le catalogue du
 * compte Fixer Lite — ce qui se réserve, et à quel prix.
 *
 * Rendu synchrone, sans effet ni `fetch` : la donnée vient de la réponse
 * `/shops/by-path`, déjà présente au rendu serveur. La tirer d'un appel
 * client la rendrait invisible du crawler, et le critère d'acceptation porte
 * précisément sur le HTML servi.
 */

const Section = styled.section`
  margin: 0;
  ${sectionPaddingStyle}
  background-color: ${colors.white};
`;

const Container = styled.div`
  ${containerStyle}
`;

const Heading = styled.h2`
  ${headingStyle}
  color: ${colors.bleuFonce};
  font-size: 1.5rem;
  margin-bottom: ${(p) => (p.$integre ? '8px' : '32px')};

  @media (min-width: ${breakpoints.mobile}) {
    font-size: ${(p) => (p.$integre ? '1.75rem' : '2.125rem')};
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid ${colors.borderLight};
  font-family: ${fonts.body};

  /* « display: flex » écrasait l’attribut « hidden » : les lignes repliées
     restaient visibles. */
  &[hidden] {
    display: none;
  }
`;

const Label = styled.span`
  flex: 1 1 auto;
  color: ${colors.bleuFonce};
  font-weight: 600;

  small {
    display: block;
    font-weight: 300;
    font-size: 0.85rem;
    color: ${colors.textSecondary};
  }
`;

const Price = styled.span`
  color: ${colors.bleuFonce};
  font-weight: 700;
  white-space: nowrap;
`;

const Plus = styled.button`
  margin-top: 14px;
  padding: 0;
  border: none;
  background: none;
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 15px;
  color: ${colors.bleu};
  cursor: pointer;
`;

const Note = styled.p`
  margin: 12px 0 0;
  font-family: ${fonts.body};
  font-size: 13px;
  line-height: 1.4;
  color: ${colors.textSecondary};
`;

// Le Bonus Répar au-dessus des prix : un petit encart, un titre et une ligne
// par palier. Fond blanc et filet, aucun aplat de couleur (retours de Clément
// du 14/10). Un réglage à 35 € ne suffit pas seul, d'où les seuils dits ici.
const BonusBloc = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0 0 16px;
  padding: 14px 16px;
  border: 1.5px solid ${colors.borderLight};
  border-radius: 16px;
  background: ${colors.white};
  font-family: ${fonts.body};
  font-size: 0.9rem;
  line-height: 1.45;
  color: ${colors.textSecondary};

  p {
    margin: 0;
  }
`;

const BonusIcone = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${colors.bleuPastel};

  img {
    width: 22px;
    height: 22px;
    object-fit: contain;
  }
`;

const BonusTitre = styled.p`
  margin-bottom: 4px !important;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${colors.textDark};
`;

const BonusPalier = styled.p`
  b {
    color: ${colors.bleu};
  }
`;

/** Au-delà, les lignes restent dans le HTML servi mais repliées. */
const VISIBLES = 4;

/** « 45 min », « 1 h », « 1 h 30 » — formatage local, sans dépendance date. */
export function formatDuration(minutes) {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest}`;
}

/**
 * Un prix absent n'affiche RIEN — ni « 0 € », ni « sur devis ».
 * Les deux seraient une promesse commerciale que personne n'a arbitrée.
 * `toPrice` écarte déjà le zéro pour la même raison : ici, `null` suffit.
 */
function formatPrice(price) {
  if (price === null || price === undefined) return null;
  // `formatEurosNoSpace` (utils/utils.jsx) forcerait deux décimales (« 79,00€ »)
  // même pour un prix rond : pas ce qu'on veut ici, juste le collage convention
  // ticket #35 (€ collé au chiffre, sans espace).
  return Number.isInteger(price)
    ? `${price}€`
    : `${price.toFixed(2).replace('.', ',')}€`;
}

/**
 * `integre` : rendu dans la colonne de la tête de fiche, sous la tête (lot 2,
 * fiche « direction B »), sans section ni conteneur à soi.
 */
export default function ShopPrestations({ shop, integre = false }) {
  const [tout, setTout] = useState(false);
  const services = fixerLiteServices(shop);

  const bonus = paliersDeLaFiche(shop);

  // Aucun bloc vide sans prestation : pas même le titre de section.
  if (services.length === 0) return null;

  const contenu = (
    <>
      <Heading $integre={integre}>Prestations et tarifs</Heading>
      {bonus && (
        <BonusBloc id="bonus-repar" data-testid="bonus-repar">
          <BonusIcone>
            <img src={icone('TARIF_EUROS')} alt="" />
          </BonusIcone>
          <div>
            <BonusTitre>
              Bonus Répar : {remiseMaxBonusRepar(bonus).toLowerCase()} sur votre
              facture
            </BonusTitre>
            {bonus.map((p) => (
              <BonusPalier key={p.seuil}>
                <b>{euros(p.montant)}</b> de remise dès {euros(p.seuil)} de
                réparations éligibles
              </BonusPalier>
            ))}
          </div>
        </BonusBloc>
      )}
      <List>
        {services.map((service, i) => {
          const duration = formatDuration(service.durationMin);
          const price = formatPrice(service.priceEur);
          return (
            <Item
              key={`${service.key || service.label}-${i}`}
              hidden={!tout && i >= VISIBLES}>
              <Label>
                {service.label}
                {duration && <small>{duration}</small>}
              </Label>
              {price && <Price>{price}</Price>}
            </Item>
          );
        })}
      </List>
      {!tout && services.length > VISIBLES && (
        <Plus type="button" onClick={() => setTout(true)}>
          Voir les {services.length} prestations
        </Plus>
      )}
      <Note>
        Tarifs indiqués par l’atelier. Le prix final est confirmé par l’atelier
        après diagnostic.
      </Note>
    </>
  );

  if (integre) return <div>{contenu}</div>;

  return (
    <Section>
      <Container>{contenu}</Container>
    </Section>
  );
}
